// Lifecycle presentation config for the listing-request list + detail views.
//
// The seller-service returns the canonical lifecycle projection — `stage`,
// `stageStep`, `stageTotal`, `stageState` — collapsed from its 13-state machine.
// admin-web renders the SAME projection off the same wire fields; only the copy
// below differs. Never derive a stage from `requestStatus` here: a second
// hand-written collapse is exactly how the two portals drifted apart.
//
// i18n is not wired yet, so copy lives as plain strings (per repo convention).
import type { ChipColor } from '@uiKit/elements/Chip.vue'
import type { PiIconName } from '@uiKit/elements/PiIcon.vue'

// The four pipeline stages emitted by the backend (ListingRequestStage), in
// order. Owners onboard BEFORE anyone signs, so every signatory is already a
// verified owner; the request then ends at Final Approval, which hands the
// property to the Properties pipeline. There is no "listed" stage — a request is
// never on the market, its property is.
export type ListingRequestStage = 'review' | 'owner_onboarding' | 'agreement' | 'final_approval'

// How the current stage is going (ListingRequestStageState). Orthogonal to the
// stage: "sent back for revision" is the state of stage 1, not a sixth stage.
export type ListingRequestStageState =
  | 'draft'
  | 'in_progress'
  | 'attention'
  | 'completed'
  | 'rejected'
  | 'withdrawn'

// Typed stage values — the single source of truth for comparing `stage`.
// Consumers (detail container, dashboard KPIs) key off these instead of loose
// string literals so a backend rename breaks the type-check, not silently the
// counts. `satisfies` guarantees every value stays a valid ListingRequestStage.
export const LISTING_STAGE = {
  review: 'review',
  ownerOnboarding: 'owner_onboarding',
  agreement: 'agreement',
  finalApproval: 'final_approval',
} as const satisfies Record<string, ListingRequestStage>

export const LISTING_STAGE_STATE = {
  draft: 'draft',
  inProgress: 'in_progress',
  attention: 'attention',
  completed: 'completed',
  rejected: 'rejected',
  withdrawn: 'withdrawn',
} as const satisfies Record<string, ListingRequestStageState>

export interface StageMeta {
  label: string
  color: ChipColor
}

// Status chip copy per stage, for a request that is progressing normally.
//
// A finished request says "Approved for property setup", not "Listed": it ends
// by becoming a property, which is then priced and submitted before an investor
// can see it.
const STAGE_META: Record<ListingRequestStage, StageMeta> = {
  review: { label: 'Under review', color: 'dark' },
  owner_onboarding: { label: 'Owner onboarding', color: 'primary' },
  agreement: { label: 'Agreement signing', color: 'primary' },
  final_approval: { label: 'Final checks', color: 'dark' },
}

// A non-normal state overrides the stage label — the seller cares far more that
// something stopped than which stage it stopped on. `attention` is the exception:
// what is needed depends on the stage, so it resolves per stage below.
const STATE_META: Partial<Record<ListingRequestStageState, StageMeta>> = {
  // Saved part-way and never submitted — nobody is reviewing it, so it must not
  // borrow review's "Under review" label.
  draft: { label: 'Draft', color: 'neutral' },
  completed: { label: 'Approved for property setup', color: 'success' },
  rejected: { label: 'Rejected', color: 'danger' },
  withdrawn: { label: 'Withdrawn', color: 'neutral' },
}

const ATTENTION_META: Partial<Record<ListingRequestStage, StageMeta>> = {
  review: { label: 'Action required', color: 'warning' },
  agreement: { label: 'Agreement declined', color: 'warning' },
}

const ATTENTION_META_FALLBACK: StageMeta = { label: 'Action required', color: 'warning' }
export const STAGE_META_FALLBACK: StageMeta = { label: 'In progress', color: 'neutral' }

export function stageMetaOf(stage: string, state: string): StageMeta {
  if (state === LISTING_STAGE_STATE.attention) {
    return ATTENTION_META[stage as ListingRequestStage] ?? ATTENTION_META_FALLBACK
  }
  return (
    STATE_META[state as ListingRequestStageState] ??
    STAGE_META[stage as ListingRequestStage] ??
    STAGE_META_FALLBACK
  )
}

// The 4 canonical lifecycle steps rendered by RequestStageProgress. `stageStep`
// (1-4) from the backend drives done/active/upcoming.
//
// Step 3 is admin-side work the seller cannot act on, and it is shown anyway:
// folding it into "Completed" is what used to tell the seller their listing was
// ready to go live while ops still had owner onboarding and promotion to do.
export interface LifecycleStepMeta {
  step: number
  label: string
  caption: string
  /** What the seller is doing in this stage — the detail header's "Stage 2 of 4 · …". */
  headline: string
  /** What the step reads once it is behind the request ("Accepted", "Approved"). */
  doneLabel: string
}

export const LIFECYCLE_STEPS: LifecycleStepMeta[] = [
  // The caption names the price and owner checks explicitly: review settles both.
  {
    step: 1,
    label: 'Review',
    caption: 'Price, title deed & owners',
    headline: 'Review',
    doneLabel: 'Accepted',
  },
  {
    step: 2,
    label: 'Owner Onboarding',
    caption: 'Every owner verifies',
    headline: 'Owner Onboarding',
    doneLabel: 'Owner Onboarding',
  },
  {
    step: 3,
    label: 'Agreement Signing',
    caption: 'MOU, then Seller’s Agreement',
    headline: 'Agreement Signing',
    doneLabel: 'Agreement Signing',
  },
  {
    step: 4,
    label: 'Final Approval',
    caption: 'Final checks',
    headline: 'Final Approval',
    doneLabel: 'Approved',
  },
]

// Tone of the stepper's active step + the hero's accent stripe, derived from the
// stage STATE (never the stage itself).
export type StageTone = 'normal' | 'attention' | 'negative'

export function stageToneOf(state: string): StageTone {
  if (state === LISTING_STAGE_STATE.rejected || state === LISTING_STAGE_STATE.withdrawn) {
    return 'negative'
  }
  if (state === LISTING_STAGE_STATE.attention) return 'attention'
  return 'normal'
}

// True once the pipeline has finished — the stepper marks every node done rather
// than leaving the last one mid-flight.
export function isStagePipelineComplete(state: string): boolean {
  return state === LISTING_STAGE_STATE.completed
}

// Tone shared by the detail-screen surfaces (status rail, stepper, timeline).
// Maps 1:1 onto the `--p-<tone>-*` token ramps, with `neutral` = surface/muted.
export type LifecycleTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'

// Tinted-surface token pair per tone. Returned as `var(--p-*)` strings (not CSS
// classes) for the few kit components that take colors as props and apply them
// inline — `IconBox`'s `bg` / `iconColor`. Scoped CSS is still the default
// everywhere else; this exists only for that prop-driven seam.
export interface ToneSurface {
  bg: string
  fg: string
}

const TONE_SURFACES: Record<LifecycleTone, ToneSurface> = {
  neutral: { bg: 'var(--p-surface-100)', fg: 'var(--p-text-muted-color)' },
  primary: { bg: 'var(--p-primary-50)', fg: 'var(--p-primary-600)' },
  success: { bg: 'var(--p-success-50)', fg: 'var(--p-success-600)' },
  warning: { bg: 'var(--p-warning-50)', fg: 'var(--p-warning-600)' },
  danger: { bg: 'var(--p-danger-50)', fg: 'var(--p-danger-600)' },
  info: { bg: 'var(--p-info-50)', fg: 'var(--p-info-600)' },
}

export function toneSurface(tone: LifecycleTone): ToneSurface {
  return TONE_SURFACES[tone]
}

// The status maps below still speak Chip colours (the list and dialogs render
// Chips); the detail screen's soft pills speak tones. One bridge, so neither map
// is written twice. `dark` is the chip's deep-blue slot — the nearest ramp is info.
const CHIP_COLOR_TONE: Record<ChipColor, LifecycleTone> = {
  primary: 'primary',
  neutral: 'neutral',
  dark: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
}

export function toneOfChipColor(color: ChipColor): LifecycleTone {
  return CHIP_COLOR_TONE[color]
}

// The reverse bridge, for the list and dialogs that still render Chips. Info has
// no chip slot of its own; `dark` is nearest.
const TONE_CHIP_COLOR: Record<LifecycleTone, ChipColor> = {
  neutral: 'neutral',
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'dark',
}

export function chipColorOfTone(tone: LifecycleTone): ChipColor {
  return TONE_CHIP_COLOR[tone]
}

// Tone of the list-row status pill + segment bar. Unlike the chip palette above,
// the list gives every stage its own hue (Figma "Sell Requests — List View"), so
// a seller scanning the table can tell stages apart without reading the label.
const STAGE_LIST_TONE: Record<ListingRequestStage, LifecycleTone> = {
  review: 'warning',
  owner_onboarding: 'primary',
  agreement: 'info',
  final_approval: 'info',
}

export function stageListToneOf(stage: string, state: string): LifecycleTone {
  if (state === LISTING_STAGE_STATE.rejected) return 'danger'
  if (state === LISTING_STAGE_STATE.withdrawn) return 'neutral'
  if (state === LISTING_STAGE_STATE.draft) return 'neutral'
  if (state === LISTING_STAGE_STATE.attention) return 'warning'
  if (state === LISTING_STAGE_STATE.completed) return 'success'
  return STAGE_LIST_TONE[stage as ListingRequestStage] ?? 'neutral'
}

// "What happens next" copy for the detail screen's status rail. The seller's
// most-asked question on this page is "what am I waiting for?" — this answers
// it per stage instead of leaving them to infer it from the stepper.
export interface StageOutlook {
  headline: string
  detail: string
  icon: PiIconName
  tone: LifecycleTone
}

// Normal progress, one entry per stage.
const STAGE_OUTLOOK: Record<ListingRequestStage, StageOutlook> = {
  review: {
    headline: 'Under review',
    detail:
      'Our team is verifying your title deed, your property details, and every owner you declared against the title deed. Nothing needed from you — you’ll be notified as soon as there’s an update.',
    icon: 'hourglass',
    tone: 'primary',
  },
  owner_onboarding: {
    headline: 'Owners onboarding',
    detail:
      'Each owner verifies their identity and payout bank account. Signing opens once every owner is verified.',
    icon: 'users',
    tone: 'primary',
  },
  agreement: {
    headline: 'Signatures pending',
    detail: 'Every owner signs the MOU, then the Seller’s Agreement.',
    icon: 'file',
    tone: 'primary',
  },
  final_approval: {
    headline: 'Final checks',
    detail: 'Sliceal is verifying the title deed and DLD records — nothing for you to do.',
    icon: 'hourglass',
    tone: 'primary',
  },
}

// A non-normal state supersedes the stage copy. `attention` resolves per stage —
// what the seller has to do differs by where it stalled.
const STATE_OUTLOOK: Partial<Record<ListingRequestStageState, StageOutlook>> = {
  rejected: {
    headline: 'Request rejected',
    detail: 'See the reason below. You can start a fresh request once it’s resolved.',
    icon: 'times-circle',
    tone: 'danger',
  },
  withdrawn: {
    headline: 'Request withdrawn',
    detail: 'You withdrew this request. Start a new one whenever you’re ready.',
    icon: 'ban',
    tone: 'neutral',
  },
}

const ATTENTION_OUTLOOK: Partial<Record<ListingRequestStage, StageOutlook>> = {
  review: {
    headline: 'Your input needed',
    detail: 'Update the details we flagged and resubmit — the review continues straight after.',
    icon: 'exclamation-triangle',
    tone: 'warning',
  },
  // An owner declined the agreement. There is nothing to sign until the team
  // re-issues it — telling the seller to "sign the agreements below" here sends
  // them to a panel with no available action.
  agreement: {
    headline: 'Agreement declined',
    detail:
      'One of the owners declined the agreement. Our team is preparing a new one — we’ll let you know when it’s ready to sign.',
    icon: 'exclamation-triangle',
    tone: 'warning',
  },
}

export const STAGE_OUTLOOK_FALLBACK: StageOutlook = {
  headline: 'In progress',
  detail: 'We’re working through this request. Check the activity log below for the latest step.',
  icon: 'clock',
  tone: 'primary',
}

export function stageOutlookOf(stage: string, state: string): StageOutlook {
  if (state === LISTING_STAGE_STATE.attention) {
    return ATTENTION_OUTLOOK[stage as ListingRequestStage] ?? STAGE_OUTLOOK_FALLBACK
  }
  return (
    STATE_OUTLOOK[state as ListingRequestStageState] ??
    STAGE_OUTLOOK[stage as ListingRequestStage] ??
    STAGE_OUTLOOK_FALLBACK
  )
}

// The not-yet-happened entry closing the detail screen's activity log: what the
// request is waiting on next. UI copy about the pipeline, like the outlook above —
// never a fabricated event. Closed requests (listed, rejected, withdrawn) have
// nothing pending, so they resolve to null and the log simply ends.
const PENDING_STEP_LABEL: Partial<Record<ListingRequestStage, string>> = {
  review: 'Awaiting approval',
  owner_onboarding: 'Awaiting owner verification',
  agreement: 'Awaiting signatures',
  final_approval: 'Awaiting final approval',
}

const ATTENTION_PENDING_STEP_LABEL: Partial<Record<ListingRequestStage, string>> = {
  review: 'Awaiting your changes',
  agreement: 'Awaiting a new agreement',
}

export function pendingStepLabelOf(stage: string, state: string): string | null {
  if (state === LISTING_STAGE_STATE.attention) {
    return ATTENTION_PENDING_STEP_LABEL[stage as ListingRequestStage] ?? null
  }
  if (state !== LISTING_STAGE_STATE.inProgress) return null
  return PENDING_STEP_LABEL[stage as ListingRequestStage] ?? null
}

// Short list-row guidance for off-path states. The list DTO doesn't carry the
// full revision/rejection text (that's detail-only), so this is UI copy that
// points the seller to the detail page — never fabricated request data.
const ATTENTION_GUIDANCE: Partial<Record<ListingRequestStage, string>> = {
  review: 'Changes requested — open to see what needs fixing.',
  agreement: 'An owner declined the agreement — open for details.',
}

const STATE_GUIDANCE: Partial<Record<ListingRequestStageState, string>> = {
  rejected: 'This request was rejected — open for the reason.',
  withdrawn: 'You withdrew this request.',
}

export function stageGuidanceOf(stage: string, state: string): string | null {
  if (state === LISTING_STAGE_STATE.attention) {
    return ATTENTION_GUIDANCE[stage as ListingRequestStage] ?? null
  }
  return STATE_GUIDANCE[state as ListingRequestStageState] ?? null
}

// Clickable KPI cards above the Sell Request table — each is both a count and
// the list's status filter. One table drives both, so a card's number and the
// rows it filters to can never disagree. Values are raw `requestStatus` (what the
// list endpoint filters on); an empty `statuses` means every request.
//
// "In progress" includes the retired statuses (never entered today): a row
// stranded in one is still in flight, so it belongs there rather than nowhere.
export type ListingStatusKpiKey =
  | 'all'
  | 'drafts'
  | 'inProgress'
  | 'handedOver'
  | 'rejected'
  | 'withdrawn'

export interface ListingStatusKpi {
  key: ListingStatusKpiKey
  label: string
  statuses: string[]
}

// The unfiltered card — also the fallback for an unknown `?filter=`.
export const LISTING_STATUS_KPI_ALL: ListingStatusKpi = {
  key: 'all',
  label: 'All requests',
  statuses: [],
}

// The in-progress statuses, grouped by the stage each sits in — seller-service's
// LISTING_REQUEST_STAGE_MAP. The dashboard's "Requests in Progress" tile breaks
// its count down by these groups; the "In progress" card below is their union,
// so the breakdown always adds up to the headline.
export type InProgressStageKey = 'review' | 'onboarding' | 'signing' | 'finalApproval'

export const IN_PROGRESS_STAGE_STATUSES: Record<InProgressStageKey, string[]> = {
  review: ['submitted', 'under_review', 'revision_requested'],
  // `approved` and `owner_verification` are retired, but meant "review is done".
  onboarding: ['approved', 'owner_verification', 'owners_onboarding'],
  // A declined agreement is re-issued and signed again — still this stage.
  signing: ['agreement_in_progress', 'agreement_rejected'],
  finalApproval: ['agreement_completed'],
}

// Named because the dashboard's "Requests in Progress" tile counts the same group:
// two definitions of "in progress" is how the two pages came to disagree.
export const LISTING_STATUS_KPI_IN_PROGRESS: ListingStatusKpi = {
  key: 'inProgress',
  label: 'In progress',
  statuses: Object.values(IN_PROGRESS_STAGE_STATUSES).flat(),
}

export const LISTING_STATUS_KPIS: ListingStatusKpi[] = [
  LISTING_STATUS_KPI_ALL,
  // Second, beside "All": a draft is the one row the seller still has to act on
  // before anyone else can.
  { key: 'drafts', label: 'Drafts', statuses: ['draft'] },
  LISTING_STATUS_KPI_IN_PROGRESS,
  { key: 'handedOver', label: 'Approved', statuses: ['promoted'] },
  { key: 'rejected', label: 'Rejected', statuses: ['rejected'] },
  { key: 'withdrawn', label: 'Withdrawn', statuses: ['withdrawn'] },
]

// Human labels for timeline event types (RequestEventType). Fallback prettifies
// any unknown snake_case type.
export const EVENT_TYPE_LABELS: Record<string, string> = {
  status_changed: 'Status updated',
  price_proposed_by_seller: 'You proposed a price',
  price_countered_by_admin: 'Sliceal proposed a price',
  price_accepted: 'Price accepted',
  price_rejected: 'Price rejected',
  document_uploaded: 'Document uploaded',
  note_added: 'Note added',
  agreement_generated: 'Agreement generated',
  agreement_sent: 'Agreement sent for signing',
  agreement_signed: 'Agreement signed',
  agreement_declined: 'Agreement declined',
  agreement_completed: 'Agreement completed',
  // Written by admin-service when the request becomes a property. The fallback
  // prettifier would render "Property promoted", our internal word for it.
  property_promoted: 'Approved for property setup',
}

export function eventLabelOf(eventType: string): string {
  return (
    EVENT_TYPE_LABELS[eventType] ??
    eventType.replace(/_/g, ' ').replace(/^./, (char) => char.toUpperCase())
  )
}

// Icon + tone per timeline event type, so the activity log is scannable at a
// glance (green = progressed, amber = waiting on someone, red = blocked).
export interface EventVisual {
  icon: PiIconName
  tone: LifecycleTone
}

export const EVENT_VISUALS: Record<string, EventVisual> = {
  status_changed: { icon: 'refresh', tone: 'info' },
  price_proposed_by_seller: { icon: 'tag', tone: 'primary' },
  price_countered_by_admin: { icon: 'tag', tone: 'warning' },
  price_accepted: { icon: 'check-circle', tone: 'success' },
  price_rejected: { icon: 'times-circle', tone: 'danger' },
  document_uploaded: { icon: 'upload', tone: 'info' },
  note_added: { icon: 'comments', tone: 'neutral' },
  agreement_generated: { icon: 'file', tone: 'neutral' },
  agreement_sent: { icon: 'send', tone: 'warning' },
  agreement_signed: { icon: 'verified', tone: 'success' },
  agreement_declined: { icon: 'ban', tone: 'danger' },
  agreement_completed: { icon: 'check-circle', tone: 'success' },
  property_promoted: { icon: 'arrow-right', tone: 'success' },
}

export const EVENT_VISUAL_FALLBACK: EventVisual = { icon: 'circle', tone: 'neutral' }

export function eventVisualOf(eventType: string): EventVisual {
  return EVENT_VISUALS[eventType] ?? EVENT_VISUAL_FALLBACK
}

// Agreement statuses the detail screen branches on (AgreementStatus).
export const AGREEMENT_STATUS = {
  REJECTED: 'rejected',
  EXPIRED: 'expired',
  VOIDED: 'voided',
} as const

// Signatory signing statuses the detail screen branches on (SignatoryStatus).
export const SIGNATURE_STATUS = {
  SIGNED: 'signed',
  DECLINED: 'declined',
} as const

// Agreement status → chip color (AgreementStatus). Fallback = neutral.
export const AGREEMENT_STATUS_META: Record<string, StageMeta> = {
  draft: { label: 'Draft', color: 'neutral' },
  generated: { label: 'Generated', color: 'neutral' },
  sent: { label: 'Awaiting signature', color: 'warning' },
  partially_signed: { label: 'Partially signed', color: 'warning' },
  signed: { label: 'Signed', color: 'primary' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'danger' },
  expired: { label: 'Expired', color: 'danger' },
  voided: { label: 'Voided', color: 'neutral' },
}

export function agreementStatusMetaOf(status: string): StageMeta {
  return AGREEMENT_STATUS_META[status] ?? STAGE_META_FALLBACK
}

// Human labels for agreement types (AgreementType).
//
// `window_extension` is a third type rather than a third step: it is issued long
// after the other two are approved, when the raise is live and running out of
// time to fund. The seller signs it on exactly the path they sign the other two,
// so it needs no flow of its own here — only a name, or the fallback below would
// show them "WINDOW_EXTENSION".
export const AGREEMENT_TYPE_LABELS: Record<string, string> = {
  mou: 'MOU',
  seller_agreement: "Seller's Agreement",
  window_extension: 'Funding Window Extension',
}

export function agreementTypeLabelOf(type: string): string {
  return AGREEMENT_TYPE_LABELS[type] ?? type.toUpperCase()
}

// Signatory signing status → chip label + color (SignatoryStatus).
export const SIGNATORY_STATUS_META: Record<string, StageMeta> = {
  pending: { label: 'Pending', color: 'neutral' },
  sent: { label: 'Sent', color: 'warning' },
  viewed: { label: 'Viewed', color: 'warning' },
  signed: { label: 'Signed', color: 'success' },
  declined: { label: 'Declined', color: 'danger' },
}

export function signatoryStatusMetaOf(status: string): StageMeta {
  return SIGNATORY_STATUS_META[status] ?? STAGE_META_FALLBACK
}

// Signatory roles (AgreementSignatoryRole). `PLATFORM` is Sliceal's own signature
// on the agreement — the seller sees that row's status but never acts on it, so
// nothing here may offer them its signing link. That signature is minted and
// forwarded from the admin portal.
export const AGREEMENT_SIGNATORY_ROLE = {
  PRIMARY_OWNER: 'primary_owner',
  CO_OWNER: 'co_owner',
  PLATFORM: 'platform',
  WITNESS: 'witness',
} as const

// Page size for the paginated list (useInfiniteQuery).
export const LISTINGS_PAGE_LIMIT = 10

// ── Phase B seller-action guards (mirror the backend preconditions) ─────────

// The `requestStatus` of a request saved part-way and never submitted. It is
// continued in the wizard or discarded — never withdrawn, revised or shown on the
// detail screen.
export const DRAFT_STATUS = 'draft'

// The stage at which every declared owner — the seller included — onboards.
export const OWNERS_ONBOARDING_STATUS = 'owners_onboarding'

// `requestStatus` values from which the seller may withdraw their own request.
// No `draft`: an unsubmitted request is discarded instead, leaving no
// "withdrawn" row behind.
export const WITHDRAWABLE_STATUSES: ReadonlySet<string> = new Set([
  'submitted',
  'under_review',
  'revision_requested',
])

// The only `requestStatus` that permits an edit-&-resubmit.
export const REVISABLE_STATUS = 'revision_requested'

// The `priceNegotiationStatus` that means there's an open admin proposal to
// accept or reject.
export const PRICE_PROPOSAL_OPEN_STATUS = 'proposed'

// The `priceNegotiationStatus` once the seller accepted Sliceal's proposal.
export const PRICE_ACCEPTED_STATUS = 'accepted'
